import { useRef, useState } from "react";
import { MAX_PROMPT_LENGTH } from "@hatch/shared";
import { DEFAULT_FRAMEWORK, FRAMEWORK_OPTIONS, FRAMEWORK_PICKER_VISIBLE, type Framework } from "../../lib/frameworks.js";
import { PROMPT_GALLERY } from "../../lib/promptGallery.js";
import { startBuild } from "../../lib/useSessionStream.js";

/**
 * One prompt box, and a shelf of prompts to start from.
 *
 * Two controls and no configuration beyond them — the entire product promise is
 * that a description is enough, and both of these are choices about WHAT gets
 * built rather than settings for Hatch (see lib/frameworks.ts and
 * lib/frameworks.ts for the test each one had to pass to earn its place). The gallery is a shortcut to a starting point and
 * nothing more: picking a card fills the box with editable text, so the user
 * is always submitting their own prompt rather than choosing a template.
 */
export function PromptView({ onStarted }: { onStarted: (sessionId: string) => void }) {
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [framework, setFramework] = useState<Framework>(DEFAULT_FRAMEWORK);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = prompt.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    const result = await startBuild(trimmed, framework);
    if ("error" in result) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    onStarted(result.sessionId);
  }

  /** Fill the box and hand the user the cursor — a start, not a commitment. */
  function choose(text: string) {
    setPrompt(text);
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    box.setSelectionRange(text.length, text.length);
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-6 pb-24 pt-20 sm:pt-28">
      <header className="mx-auto max-w-2xl text-center">
        <h1 className="font-display text-5xl tracking-tight sm:text-6xl">Hatch</h1>
        <p className="mx-auto mt-5 max-w-lg text-lg leading-relaxed text-ink-soft">
          Describe a website. A builder will make it and put it online — you can watch the
          whole thing happen.
        </p>
      </header>

      <form onSubmit={submit} className="mx-auto mt-12 max-w-2xl">
        <label htmlFor="prompt" className="sr-only">
          Describe the website you want
        </label>
        <div className="rounded-2xl border border-rule bg-surface p-2 shadow-sm transition-colors focus-within:border-accent">
          <textarea
            id="prompt"
            ref={boxRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH))}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit(e);
            }}
            rows={4}
            autoFocus
            disabled={submitting}
            placeholder="A site for…"
            className="w-full resize-none bg-transparent p-4 text-lg leading-relaxed outline-none placeholder:text-ink-faint disabled:opacity-60"
          />

          <div className="flex items-center justify-between gap-4 px-4 pb-2">
            <div className="flex items-center gap-3">
              {/*
                The framework choice, currently not shown at all — see
                FRAMEWORK_PICKER_VISIBLE in lib/frameworks.ts, which is false
                while a Svelte build cannot finish. The form sends
                DEFAULT_FRAMEWORK and the row collapses to the counter.

                Kept rather than deleted: this is the markup that comes back
                when the flag flips, and a radiogroup rather than a <select> so
                both options are visible without a click. Roving focus is
                deliberately not implemented: two options, both tabbable, is
                simpler and no worse to use.
              */}
              {FRAMEWORK_PICKER_VISIBLE && (
                <div
                  role="radiogroup"
                  aria-label="Frontend framework"
                  className="flex rounded-full border border-rule p-0.5"
                >
                  {FRAMEWORK_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={framework === option.value}
                      title={option.hint}
                      disabled={submitting}
                      onClick={() => setFramework(option.value)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        framework === option.value
                          ? "bg-accent text-white"
                          : "text-ink-faint hover:text-ink-soft"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              <span className="text-xs tabular-nums text-ink-faint">
                {trimmed.length}/{MAX_PROMPT_LENGTH}
              </span>
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-full bg-accent px-7 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-35"
            >
              {submitting ? "Hatching…" : "Hatch it"}
            </button>
          </div>
        </div>

        {error && (
          <p role="alert" className="mt-4 text-sm text-halt">
            {error}
          </p>
        )}
      </form>

      <section className="mt-20" aria-labelledby="gallery-heading">
        <h2
          id="gallery-heading"
          className="text-center text-xs uppercase tracking-[0.16em] text-ink-faint"
        >
          Or start from one of these
        </h2>

        <div className="mt-8 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {PROMPT_GALLERY.map((group) => (
            <div key={group.name}>
              <h3 className="font-display text-sm text-ink-soft">{group.name}</h3>
              <ul className="mt-3 space-y-2">
                {group.prompts.map((item) => (
                  <li key={item.title}>
                    <button
                      type="button"
                      onClick={() => choose(item.prompt)}
                      disabled={submitting}
                      title={item.prompt}
                      className="w-full rounded-xl border border-rule bg-surface px-4 py-3 text-left transition-colors hover:border-accent hover:bg-accent-soft disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className="block text-sm font-medium">{item.title}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-ink-faint">
                        {item.prompt}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
